import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FellowClient, FellowDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new FellowClient(API_BASE_URL, axiosInstance);

// Query hook - Get fellows for dropdowns (cached 4 hours)
// Pass canWriteEndorsementMotivations: true to only fetch fellows allowed to write endorsement motivations
export const useFellows = (filters?: { canWriteEndorsementMotivations?: boolean }) => {
  return useQuery({
    queryKey: ['fellows', filters?.canWriteEndorsementMotivations ?? null],
    queryFn: async () => {
      const response = await apiClient.searchFellows({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Name',
        filters: filters?.canWriteEndorsementMotivations !== undefined
          ? { canWriteEndorsementMotivations: filters.canWriteEndorsementMotivations }
          : undefined,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useFellowSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchFellows({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single fellow by ID
export const useFellowById = (id: string) => {
  return useQuery({
    queryKey: ['fellows', id],
    queryFn: async () => {
      const response = await apiClient.getFellowById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create fellow
export const useCreateFellow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FellowDTO) => {
      await apiClient.createFellow(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fellows'] });
    },
  });
};

// Mutation hook - Update fellow
export const useUpdateFellow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FellowDTO }) => {
      await apiClient.updateFellow(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fellows'] });
    },
  });
};

// Mutation hook - Delete fellow
export const useDeleteFellow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteFellow(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fellows'] });
    },
  });
};
